import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTheme } from '../contexts/ThemeContext';
import { C } from '../constants';

// Source tools where founders gather information - with recognizable logo SVGs
const SOURCES = [
  { 
    id: 'chatgpt', 
    name: 'ChatGPT', 
    color: '#10A37F',
    logo: (
      <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
        <path d="M22.282 9.821a5.985 5.985 0 0 0-.516-4.91 6.046 6.046 0 0 0-6.51-2.9A6.065 6.065 0 0 0 4.981 4.18a5.985 5.985 0 0 0-3.998 2.9 6.046 6.046 0 0 0 .743 7.097 5.98 5.98 0 0 0 .51 4.911 6.051 6.051 0 0 0 6.515 2.9A5.985 5.985 0 0 0 13.26 24a6.056 6.056 0 0 0 5.772-4.206 5.99 5.99 0 0 0 3.997-2.9 6.056 6.056 0 0 0-.747-7.073zM13.26 22.43a4.476 4.476 0 0 1-2.876-1.04l.141-.081 4.779-2.758a.795.795 0 0 0 .392-.681v-6.737l2.02 1.168a.071.071 0 0 1 .038.052v5.583a4.504 4.504 0 0 1-4.494 4.494zM3.6 18.304a4.47 4.47 0 0 1-.535-3.014l.142.085 4.783 2.759a.771.771 0 0 0 .78 0l5.843-3.369v2.332a.08.08 0 0 1-.033.062L9.74 19.95a4.5 4.5 0 0 1-6.14-1.646zM2.34 7.896a4.485 4.485 0 0 1 2.366-1.973V11.6a.766.766 0 0 0 .388.676l5.815 3.355-2.02 1.168a.076.076 0 0 1-.071 0l-4.83-2.786A4.504 4.504 0 0 1 2.34 7.896zm16.597 3.855l-5.833-3.387L15.119 7.2a.076.076 0 0 1 .071 0l4.83 2.791a4.494 4.494 0 0 1-.676 8.105v-5.678a.79.79 0 0 0-.407-.667zm2.01-3.023l-.141-.085-4.774-2.782a.776.776 0 0 0-.785 0L9.409 9.23V6.897a.066.066 0 0 1 .028-.061l4.83-2.787a4.5 4.5 0 0 1 6.68 4.66zm-12.64 4.135l-2.02-1.164a.08.08 0 0 1-.038-.057V6.075a4.5 4.5 0 0 1 7.375-3.453l-.142.08L8.704 5.46a.795.795 0 0 0-.393.681zm1.097-2.365l2.602-1.5 2.607 1.5v2.999l-2.597 1.5-2.607-1.5z"/>
      </svg>
    )
  },
  { 
    id: 'perplexity', 
    name: 'Perplexity', 
    color: '#20B2AA',
    logo: (
      <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
        <path d="M12 2L4 7v10l8 5 8-5V7l-8-5zm0 2.5L17.5 8 12 11.5 6.5 8 12 4.5zM6 9.5l5 3v6l-5-3v-6zm12 0v6l-5 3v-6l5-3z"/>
      </svg>
    )
  },
  { 
    id: 'google', 
    name: 'Google', 
    color: '#4285F4',
    logo: (
      <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
        <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
        <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
      </svg>
    )
  },
  { 
    id: 'notes', 
    name: 'Notes', 
    color: '#FFB800',
    logo: (
      <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
        <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V5h14v14zM7 7h10v2H7zm0 4h10v2H7zm0 4h7v2H7z"/>
      </svg>
    )
  },
  { 
    id: 'docs', 
    name: 'Docs', 
    color: '#4688F1',
    logo: (
      <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
        <path d="M14 2H6c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V8l-6-6zm4 18H6V4h7v5h5v11zM8 12h8v2H8zm0 4h8v2H8zm0-8h5v2H8z"/>
      </svg>
    )
  },
];

// Deck tools where they try to compile
const DECK_TOOLS = [
  { 
    id: 'canva', 
    name: 'Canva', 
    color: '#00C4CC',
    logo: (
      <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
        <circle cx="12" cy="12" r="10"/>
      </svg>
    )
  },
  { 
    id: 'gamma', 
    name: 'Gamma', 
    color: '#8B5CF6',
    logo: (
      <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
        <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
      </svg>
    )
  },
  { 
    id: 'pitch', 
    name: 'Pitch', 
    color: '#F43F5E',
    logo: (
      <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
        <polygon points="5,3 19,12 5,21"/>
      </svg>
    )
  },
];

// Mobile Info Fragment - smaller radius, better spacing
const MobileInfoFragment = ({ source, index, phase, totalSources }) => {
  // Spread icons in a larger circle to prevent overlap
  const angle = (index / totalSources) * 360;
  const radius = 50;
  const startX = Math.cos((angle - 90) * Math.PI / 180) * radius;
  const startY = Math.sin((angle - 90) * Math.PI / 180) * radius;
  
  return (
    <motion.div
      initial={{ x: startX, y: startY, scale: 0, opacity: 0 }}
      animate={phase === 'gather' ? {
        x: startX, y: startY, scale: 1, opacity: 1,
      } : phase === 'converge' ? {
        x: (index - 2) * 12,
        y: 0,
        scale: 0.6,
        opacity: 0.8,
      } : {
        x: 0, y: 30, scale: 0.3, opacity: 0,
      }}
      transition={{ duration: 0.5, delay: phase === 'gather' ? index * 0.1 : index * 0.05 }}
      style={{
        position: 'absolute',
        left: '50%',
        top: '50%',
        marginLeft: '-16px',
        marginTop: '-16px',
        zIndex: 10 + index,
      }}
    >
      <div
        style={{
          width: '32px',
          height: '32px',
          borderRadius: '8px',
          background: `linear-gradient(135deg, ${source.color}, ${source.color}90)`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: `0 2px 10px ${source.color}40`,
          border: '1px solid rgba(255,255,255,0.2)',
          color: 'white',
          overflow: 'hidden',
        }}
      >
        <div style={{ 
          width: '16px', 
          height: '16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          {source.logo}
        </div>
      </div>
    </motion.div>
  );
};

// Mobile Deck Output
const MobileDeckOutput = ({ tool, index, isActive, result, theme }) => (
  <motion.div
    initial={{ opacity: 0, scale: 0.9 }}
    animate={{ opacity: isActive ? 1 : 0.4, scale: isActive ? 1 : 0.95 }}
    transition={{ duration: 0.3, delay: index * 0.1 }}
    style={{
      padding: '8px 12px',
      background: theme === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)',
      border: `2px solid ${isActive ? tool.color : 'transparent'}`,
      borderRadius: '10px',
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
    }}
  >
    <div
      style={{
        width: '20px',
        height: '20px',
        borderRadius: '6px',
        background: tool.color,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '8px',
        fontWeight: 700,
        color: 'white',
      }}
    >
      {tool.name.charAt(0)}
    </div>
    <span style={{ fontSize: '11px', fontWeight: 600 }}>{tool.name}</span>
    {isActive && result && (
      <span style={{ fontSize: '8px', color: C.red, fontWeight: 600 }}>!</span>
    )}
  </motion.div>
);

// Mobile Founder Icon - smaller
const MobileFounderIcon = ({ phase, theme }) => (
  <motion.div
    animate={{ scale: phase === 'converge' ? [1, 1.1, 1] : 1 }}
    transition={{ duration: 0.5 }}
    style={{
      width: '50px',
      height: '50px',
      borderRadius: '50%',
      background: theme === 'dark' ? 'linear-gradient(135deg, #1a1a2e, #16213e)' : 'linear-gradient(135deg, #f0f4f8, #e2e8f0)',
      border: `2px solid ${theme === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.1)'}`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      boxShadow: theme === 'dark' ? '0 6px 24px rgba(0,0,0,0.4)' : '0 6px 24px rgba(0,0,0,0.1)',
      position: 'relative',
      zIndex: 20,
    }}
  >
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="8" r="4" fill={theme === 'dark' ? '#fff' : '#333'} opacity="0.8"/>
      <path d="M4 20c0-4 4-6 8-6s8 2 8 6" stroke={theme === 'dark' ? '#fff' : '#333'} strokeWidth="2" strokeLinecap="round" opacity="0.8"/>
    </svg>
    {phase === 'converge' && (
      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        style={{
          position: 'absolute',
          top: '-6px',
          right: '-6px',
          width: '20px',
          height: '20px',
          borderRadius: '50%',
          background: '#FFB800',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '10px',
          fontWeight: 700,
        }}
      >
        ?
      </motion.div>
    )}
  </motion.div>
);

// Mobile Poor Deck Result
const MobilePoorDeckResult = ({ isVisible, theme }) => (
  <motion.div
    initial={{ opacity: 0, scale: 0.9 }}
    animate={{ opacity: isVisible ? 1 : 0, scale: isVisible ? 1 : 0.9 }}
    transition={{ duration: 0.4 }}
    style={{
      background: theme === 'dark' ? '#1a1a2e' : '#ffffff',
      borderRadius: '10px',
      padding: '12px',
      border: `1px solid ${theme === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
      width: '100%',
      maxWidth: '280px',
    }}
  >
    <div style={{ display: 'flex', gap: '4px', marginBottom: '8px' }}>
      <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#EF4444' }} />
      <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#F59E0B' }} />
      <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#10B981' }} />
      <span style={{ marginLeft: 'auto', fontSize: '8px', color: C.red, fontWeight: 600 }}>Fragmented</span>
    </div>
    <div style={{ display: 'flex', gap: '4px', height: '50px', alignItems: 'flex-end' }}>
      {[60, 80, 50, 90, 70].map((h, i) => (
        <motion.div
          key={i}
          initial={{ height: 0 }}
          animate={{ height: isVisible ? `${h}%` : 0 }}
          transition={{ duration: 0.3, delay: i * 0.05 }}
          style={{ flex: 1, background: ['#3B82F6', '#1E40AF', '#60A5FA', '#93C5FD', '#2563EB'][i], borderRadius: '3px' }}
        />
      ))}
    </div>
    {isVisible && (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.5 }}
        style={{
          marginTop: '8px',
          padding: '6px 10px',
          background: `${C.red}15`,
          borderRadius: '6px',
          border: `1px solid ${C.red}30`,
          fontSize: '9px',
          color: C.red,
          fontWeight: 600,
          textAlign: 'center',
        }}
      >
        No consistency. No close.
      </motion.div>
    )}
  </motion.div>
);

// Floating info fragment - scattered on LEFT, flows RIGHT towards arrow
const InfoFragment = ({ source, index, phase, totalSources }) => {
  // Scattered positions - spread out more to prevent ANY overlap
  // Each icon needs ~70px vertical space minimum
  const scatterPositions = [
    { x: -30, y: -130 },  // ChatGPT - top (isolated)
    { x: -110, y: -60 },  // Perplexity - upper left
    { x: -50, y: 10 },    // Google - middle right
    { x: -120, y: 50 },   // Notes - lower left  
    { x: -60, y: 120 },   // Docs - bottom (isolated)
  ];
  
  const startPos = scatterPositions[index] || { x: -80, y: (index - 2) * 80 };
  
  return (
    <motion.div
      initial={{ 
        x: startPos.x - 40, 
        y: startPos.y, 
        scale: 0, 
        opacity: 0,
      }}
      animate={phase === 'gather' ? {
        x: startPos.x,
        y: startPos.y,
        scale: 1,
        opacity: 1,
      } : phase === 'converge' ? {
        x: 60,
        y: 0,
        scale: 0.5,
        opacity: 0.85,
      } : {
        x: 150,
        y: 0,
        scale: 0.25,
        opacity: 0,
      }}
      transition={{
        duration: 0.7,
        delay: phase === 'gather' ? index * 0.12 : index * 0.06,
        ease: "easeOut"
      }}
      style={{
        position: 'absolute',
        left: '50%',
        top: '50%',
        zIndex: 10 + index,
        marginLeft: '-26px',
        marginTop: '-26px',
      }}
    >
      <div
        style={{
          width: '52px',
          height: '52px',
          borderRadius: '12px',
          background: `linear-gradient(135deg, ${source.color}, ${source.color}90)`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: `0 6px 24px ${source.color}50`,
          border: '2px solid rgba(255,255,255,0.25)',
          color: 'white',
        }}
      >
        {source.logo}
      </div>
      {/* Label - positioned to the right of icon to avoid vertical overlap */}
      <AnimatePresence>
        {phase === 'gather' && (
          <motion.div
            initial={{ opacity: 0, x: -5 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0 }}
            transition={{ delay: 0.3, duration: 0.2 }}
            style={{
              position: 'absolute',
              top: '50%',
              left: '100%',
              transform: 'translateY(-50%)',
              marginLeft: '8px',
              padding: '4px 8px',
              background: 'rgba(0,0,0,0.75)',
              borderRadius: '6px',
              fontSize: '10px',
              fontWeight: 600,
              whiteSpace: 'nowrap',
              color: 'white',
            }}
          >
            {source.name}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

// Founder/User icon in center - BIGGER
const FounderIcon = ({ phase, theme }) => {
  return (
    <motion.div
      animate={{
        scale: phase === 'converge' ? [1, 1.15, 1] : 1,
      }}
      transition={{ duration: 0.5 }}
      style={{
        width: '80px',
        height: '80px',
        borderRadius: '50%',
        background: theme === 'dark' 
          ? 'linear-gradient(135deg, #1a1a2e, #16213e)'
          : 'linear-gradient(135deg, #f0f4f8, #e2e8f0)',
        border: `3px solid ${theme === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.1)'}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxShadow: theme === 'dark' 
          ? '0 10px 40px rgba(0,0,0,0.5)' 
          : '0 10px 40px rgba(0,0,0,0.12)',
        position: 'relative',
        zIndex: 20,
      }}
    >
      <svg width="36" height="36" viewBox="0 0 24 24" fill="none">
        <circle cx="12" cy="8" r="4" fill={theme === 'dark' ? '#fff' : '#333'} opacity="0.8"/>
        <path d="M4 20c0-4 4-6 8-6s8 2 8 6" stroke={theme === 'dark' ? '#fff' : '#333'} strokeWidth="2" strokeLinecap="round" opacity="0.8"/>
      </svg>
      {phase === 'converge' && (
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          style={{
            position: 'absolute',
            top: '-10px',
            right: '-10px',
            width: '28px',
            height: '28px',
            borderRadius: '50%',
            background: '#FFB800',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '14px',
            fontWeight: 700,
          }}
        >
          ?
        </motion.div>
      )}
    </motion.div>
  );
};

// Deck tool output - BIGGER
const DeckOutput = ({ tool, index, isActive, result }) => {
  const { theme } = useTheme();
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ 
        opacity: isActive ? 1 : 0.4, 
        y: 0,
        scale: isActive ? 1 : 0.95,
      }}
      transition={{ duration: 0.4, delay: index * 0.1 }}
      style={{
        padding: '14px 20px',
        background: theme === 'dark' 
          ? 'rgba(255,255,255,0.05)' 
          : 'rgba(0,0,0,0.03)',
        border: `2px solid ${isActive ? tool.color : 'transparent'}`,
        borderRadius: '14px',
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        minWidth: '150px',
      }}
    >
      <div
        style={{
          width: '36px',
          height: '36px',
          borderRadius: '10px',
          background: tool.color,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '14px',
          fontWeight: 700,
          color: 'white',
        }}
      >
        {tool.name.charAt(0)}
      </div>
      <div>
        <div style={{ fontSize: '13px', fontWeight: 600, opacity: 0.9 }}>
          {tool.name}
        </div>
        {isActive && result && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            style={{ 
              fontSize: '10px', 
              color: result === 'poor' ? C.red : C.yellow,
              fontWeight: 600,
            }}
          >
            {result === 'poor' ? 'Messy Output' : 'Inconsistent'}
          </motion.div>
        )}
      </div>
    </motion.div>
  );
};

// Poor deck result visualization - BIGGER
const PoorDeckResult = ({ isVisible, theme }) => {
  const slides = [
    { color: '#3B82F6', height: '100%' },
    { color: '#1E40AF', height: '80%' },
    { color: '#60A5FA', height: '60%' },
    { color: '#93C5FD', height: '90%' },
    { color: '#2563EB', height: '70%' },
  ];
  
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ 
        opacity: isVisible ? 1 : 0, 
        scale: isVisible ? 1 : 0.9 
      }}
      transition={{ duration: 0.5 }}
      style={{
        background: theme === 'dark' ? '#1a1a2e' : '#ffffff',
        borderRadius: '14px',
        padding: '18px',
        border: `1px solid ${theme === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
        boxShadow: theme === 'dark' 
          ? '0 10px 40px rgba(0,0,0,0.35)' 
          : '0 10px 40px rgba(0,0,0,0.1)',
        width: '100%',
        maxWidth: '320px',
      }}
    >
      {/* Window controls */}
      <div style={{ display: 'flex', gap: '7px', marginBottom: '14px' }}>
        <div style={{ width: '12px', height: '12px', borderRadius: '50%', background: '#EF4444' }} />
        <div style={{ width: '12px', height: '12px', borderRadius: '50%', background: '#F59E0B' }} />
        <div style={{ width: '12px', height: '12px', borderRadius: '50%', background: '#10B981' }} />
        <span style={{ 
          marginLeft: 'auto', 
          fontSize: '10px', 
          color: C.red, 
          fontWeight: 600 
        }}>
          Fragmented Deck
        </span>
      </div>
      
      {/* Messy slides */}
      <div style={{ 
        display: 'flex', 
        gap: '10px', 
        alignItems: 'flex-end',
        height: '100px',
      }}>
        {slides.map((slide, i) => (
          <motion.div
            key={i}
            initial={{ height: 0 }}
            animate={{ height: isVisible ? slide.height : 0 }}
            transition={{ 
              duration: 0.4, 
              delay: isVisible ? i * 0.1 : 0,
              ease: "easeOut"
            }}
            style={{
              flex: 1,
              background: slide.color,
              borderRadius: '5px',
              position: 'relative',
            }}
          >
            {/* Random "content" lines */}
            <div style={{ 
              position: 'absolute', 
              top: '20%', 
              left: '15%', 
              right: '15%',
              display: 'flex',
              flexDirection: 'column',
              gap: '5px',
            }}>
              {[...Array(Math.floor(Math.random() * 3) + 1)].map((_, j) => (
                <div 
                  key={j}
                  style={{
                    height: '4px',
                    background: 'rgba(255,255,255,0.4)',
                    borderRadius: '2px',
                    width: `${60 + Math.random() * 40}%`,
                  }}
                />
              ))}
            </div>
          </motion.div>
        ))}
      </div>
      
      {/* Warning message */}
      {isVisible && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8 }}
          style={{
            marginTop: '14px',
            padding: '10px 14px',
            background: `${C.red}15`,
            borderRadius: '10px',
            border: `1px solid ${C.red}30`,
            fontSize: '12px',
            color: C.red,
            fontWeight: 600,
            textAlign: 'center',
          }}
        >
          No consistency. No story. No close.
        </motion.div>
      )}
    </motion.div>
  );
};

// Main Component
export const FragmentedIntelAnimation = () => {
  const { theme } = useTheme();
  const [phase, setPhase] = useState('idle'); // idle, gather, converge, dump, result
  const [activeTool, setActiveTool] = useState(null);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    const runCycle = async () => {
      // Reset
      setPhase('idle');
      setActiveTool(null);
      await new Promise(r => setTimeout(r, 500));
      
      // Phase 1: Gather from sources
      setPhase('gather');
      await new Promise(r => setTimeout(r, 2500));
      
      // Phase 2: Converge to founder
      setPhase('converge');
      await new Promise(r => setTimeout(r, 1500));
      
      // Phase 3: Dump to deck tools
      setPhase('dump');
      for (let i = 0; i < DECK_TOOLS.length; i++) {
        setActiveTool(i);
        await new Promise(r => setTimeout(r, 800));
      }
      
      // Phase 4: Show poor result
      setPhase('result');
      await new Promise(r => setTimeout(r, 3500));
    };

    runCycle();
    const interval = setInterval(runCycle, 10000);
    return () => clearInterval(interval);
  }, []);

  // Mobile layout - vertical stack with all labels visible
  if (isMobile) {
    return (
      <div
        style={{
          position: 'relative',
          width: '100%',
          padding: '16px 8px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '16px',
          overflow: 'visible',
        }}
        data-testid="fragmented-intel-animation"
      >
        {/* Source labels - organized in 2 rows */}
        <div style={{ 
          display: 'flex', 
          flexDirection: 'column',
          gap: '6px', 
          alignItems: 'center',
          marginBottom: '8px'
        }}>
          {/* First row - 3 items */}
          <div style={{ display: 'flex', gap: '6px', justifyContent: 'center' }}>
            {SOURCES.slice(0, 3).map((source) => (
              <motion.div
                key={source.id}
                animate={{ 
                  opacity: phase === 'gather' ? 1 : 0.4,
                  scale: phase === 'gather' ? 1 : 0.9
                }}
                style={{
                  padding: '5px 10px',
                  background: `${source.color}20`,
                  border: `1px solid ${source.color}50`,
                  borderRadius: '100px',
                  fontSize: '10px',
                  fontWeight: 600,
                  color: source.color,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '5px',
                  whiteSpace: 'nowrap',
                  lineHeight: 1,
                }}
              >
                <div style={{ 
                  width: '12px', 
                  height: '12px', 
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}>
                  {source.logo}
                </div>
                <span style={{ lineHeight: 1 }}>{source.name}</span>
              </motion.div>
            ))}
          </div>
          {/* Second row - 2 items */}
          <div style={{ display: 'flex', gap: '6px', justifyContent: 'center' }}>
            {SOURCES.slice(3).map((source) => (
              <motion.div
                key={source.id}
                animate={{ 
                  opacity: phase === 'gather' ? 1 : 0.4,
                  scale: phase === 'gather' ? 1 : 0.9
                }}
                style={{
                  padding: '5px 10px',
                  background: `${source.color}20`,
                  border: `1px solid ${source.color}50`,
                  borderRadius: '100px',
                  fontSize: '10px',
                  fontWeight: 600,
                  color: source.color,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '5px',
                  whiteSpace: 'nowrap',
                  lineHeight: 1,
                }}
              >
                <div style={{ 
                  width: '12px', 
                  height: '12px', 
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}>
                  {source.logo}
                </div>
                <span style={{ lineHeight: 1 }}>{source.name}</span>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Founder icon with converging sources */}
        <div
          style={{
            position: 'relative',
            width: '120px',
            height: '120px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <MobileFounderIcon phase={phase} theme={theme} />
          {SOURCES.map((source, i) => (
            <MobileInfoFragment
              key={source.id}
              source={source}
              index={i}
              phase={phase}
              totalSources={SOURCES.length}
            />
          ))}
        </div>
        
        {/* Status text */}
        <motion.div
          animate={{ opacity: 1 }}
          style={{
            fontSize: '11px',
            fontWeight: 600,
            color: phase === 'gather' ? C.secondary : phase === 'converge' ? '#FFB800' : theme === 'dark' ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)',
            textAlign: 'center',
          }}
        >
          {phase === 'gather' && 'Gathering from everywhere...'}
          {phase === 'converge' && 'Trying to make sense...'}
          {phase === 'dump' && 'Dumping into deck tools...'}
          {phase === 'result' && 'Result: Fragmented deck'}
        </motion.div>

        {/* Arrow down */}
        <motion.div
          animate={{
            y: phase === 'dump' || phase === 'result' ? [0, 5, 0] : 0,
            opacity: phase === 'dump' || phase === 'result' ? 1 : 0.3,
          }}
          transition={{ duration: 1, repeat: phase === 'dump' ? Infinity : 0 }}
        >
          <svg width="24" height="30" viewBox="0 0 24 30">
            <path
              d="M12 0 L12 22 M4 16 L12 26 L20 16"
              fill="none"
              stroke={C.secondary}
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </motion.div>

        {/* Deck tools row */}
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', justifyContent: 'center' }}>
          {DECK_TOOLS.map((tool, i) => (
            <MobileDeckOutput
              key={tool.id}
              tool={tool}
              index={i}
              isActive={phase === 'dump' && activeTool >= i || phase === 'result'}
              result={phase === 'result' ? 'poor' : null}
              theme={theme}
            />
          ))}
        </div>

        {/* Poor result - compact on mobile */}
        <MobilePoorDeckResult isVisible={phase === 'result'} theme={theme} />
      </div>
    );
  }

  // Desktop layout - Sources scattered on LEFT, flow to founder, then to deck tools on RIGHT
  return (
    <div
      style={{
        position: 'relative',
        width: '100%',
        padding: '40px 0',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '420px',
        overflow: 'visible',
      }}
      data-testid="fragmented-intel-animation"
    >
      {/* LEFT: Sources area - scattered fragments */}
      <div
        style={{
          position: 'relative',
          width: '380px',
          height: '420px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        {/* Source fragments - positioned absolutely within this container */}
        {SOURCES.map((source, i) => (
          <InfoFragment
            key={source.id}
            source={source}
            index={i}
            phase={phase}
            totalSources={SOURCES.length}
          />
        ))}
        
        {/* Label for gather phase */}
        <AnimatePresence>
          {phase === 'gather' && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              style={{
                position: 'absolute',
                bottom: '0',
                left: '50%',
                transform: 'translateX(-50%)',
                fontSize: '11px',
                fontWeight: 600,
                color: theme === 'dark' ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)',
                whiteSpace: 'nowrap',
              }}
            >
              Gathering from everywhere...
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* CENTER: Founder icon */}
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: '16px',
        flexShrink: 0,
      }}>
        <FounderIcon phase={phase} theme={theme} />
        
        {/* Arrow */}
        <motion.div
          animate={{
            x: phase === 'dump' || phase === 'result' ? [0, 12, 0] : 0,
            opacity: phase === 'dump' || phase === 'result' ? 1 : 0.4,
          }}
          transition={{ duration: 1, repeat: phase === 'dump' ? Infinity : 0 }}
        >
          <svg width="70" height="40" viewBox="0 0 70 40">
            <defs>
              <linearGradient id="fragArrow" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor={`${C.secondary}40`} />
                <stop offset="100%" stopColor={C.secondary} />
              </linearGradient>
            </defs>
            <path
              d="M0 20 L50 20 M40 8 L58 20 L40 32"
              fill="none"
              stroke="url(#fragArrow)"
              strokeWidth="4"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </motion.div>
      </div>

      {/* RIGHT: Deck tools and result */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', marginLeft: '16px' }}>
        {/* Deck tools */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {DECK_TOOLS.map((tool, i) => (
            <DeckOutput
              key={tool.id}
              tool={tool}
              index={i}
              isActive={phase === 'dump' && activeTool >= i || phase === 'result'}
              result={phase === 'result' ? 'poor' : null}
            />
          ))}
        </div>

        {/* Poor result */}
        <PoorDeckResult isVisible={phase === 'result'} theme={theme} />
      </div>
    </div>
  );
};

export default FragmentedIntelAnimation;
